import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AppLayout } from './components/AppLayout.tsx';
import { PublicShell } from './components/PublicShell.tsx';
import { useAuth } from './lib/auth.tsx';
import { Spinner } from './components/ui.tsx';
import { ChatsPage } from './pages/ChatsPage.tsx';
import { HelpPage } from './pages/HelpPage.tsx';
import { InventoryPage } from './pages/InventoryPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { ManagementPage } from './pages/ManagementPage.tsx';
import { RegisterPage } from './pages/RegisterPage.tsx';
import { InvitationPage } from './pages/InvitationPage.tsx';
import { PasswordResetPage } from './pages/PasswordResetPage.tsx';
import { SettingsPage } from './pages/SettingsPage.tsx';
import { ConversationPage } from './pages/ConversationPage.tsx';
import { UnsubscribePage } from './pages/UnsubscribePage.tsx';

function FullPageSpinner(): React.JSX.Element {
  return (
    <div className="flex h-full items-center justify-center text-brand-600">
      <Spinner className="h-7 w-7" />
    </div>
  );
}

/** The public help page, in the frame a signed-out reader gets. */
function PublicHelp(): React.JSX.Element {
  return (
    <PublicShell>
      <HelpPage />
    </PublicShell>
  );
}

export function App(): React.JSX.Element {
  const { me, loading } = useAuth();
  const { pathname } = useLocation();

  // Normalised the way the router matches, which tolerates /help/ and /Help —
  // a stranger typing the URL should not get a different screen for a stray
  // slash. The prefix, not the whole path: the help page carries its open tab
  // in the URL, so a link shared as /help/inbox has to take the same route
  // through here as /help does.
  const isHelp = /^\/help(\/|$)/.test(pathname.replace(/\/+$/, '').toLowerCase());

  // Routing before the session resolves would bounce a signed-in agent to the
  // login screen on every hard refresh.
  //
  // The help page is the exception, because it is now a first-touch URL: it is
  // linked from the sign-in screen, so people arrive with no account and no
  // reason to wait on a session call that is going to 401. Its prose needs
  // nothing from the server, so it paints at once. A signed-in reader who hard
  // loads it sees the public frame for the length of one request before the
  // rail arrives — worth it, because the case being bought is a stranger on a
  // cold server, which is exactly when a spinner reads as broken.
  //
  // Falling through to the signed-out tree rather than returning the page
  // directly: `me` is null while loading, so that tree already renders it, and
  // going through the same <Routes> both times means React keeps the subtree
  // when `loading` flips. Returning a bare element here instead would put a
  // different type in the same slot and remount the page, throwing away the
  // reader's tab and scroll position the moment the session call lands.
  if (loading && !isHelp) return <FullPageSpinner />;

  if (!me) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route path="/password-reset/:uuid" element={<PasswordResetPage />} />
        <Route path="/invitation/:uuid" element={<InvitationPage />} />
        <Route path="/unsubscribe/:sessionUuid" element={<UnsubscribePage />} />
        {/* The customer's own conversation, linked from every notification
            email. The person opening it has no account, so it has to be here
            rather than behind the redirect to /login. (Router 7 ranks by
            specificity, not by declaration order, so the wildcard below cannot
            shadow it wherever it sits — but keep the real routes above it, so
            reading the list top to bottom tells the truth.) */}
        <Route path="/c/:chatUuid/:sessionUuid" element={<ConversationPage />} />
        {/* The same page the signed-in app shows, in a frame that offers the
            one link a visitor here can use. Deciding whether the product suits
            you should not require an account first, and an invited agent
            reading up before their first shift has no session yet either. */}
        <Route path="/help/:tab?" element={<PublicHelp />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/unsubscribe/:sessionUuid" element={<UnsubscribePage />} />
      {/* Also reachable while signed in: an owner clicking the link in their
          own test email should see what the customer sees. */}
      <Route path="/c/:chatUuid/:sessionUuid" element={<ConversationPage />} />
      <Route element={<AppLayout />}>
        <Route path="/chats" element={<ChatsPage />} />
        <Route path="/chats/:sessionUuid" element={<ChatsPage />} />
        {/* The tab is in the path — see `useTabRoute`. Optional, so the bare
            path still resolves; the page then rewrites it to its first tab. */}
        <Route path="/management/:tab?" element={<ManagementPage />} />
        <Route path="/inventory/:tab?" element={<InventoryPage />} />
        <Route path="/settings/:tab?" element={<SettingsPage />} />
        <Route path="/help/:tab?" element={<HelpPage />} />
        <Route path="*" element={<Navigate to="/chats" replace />} />
      </Route>
    </Routes>
  );
}
